/**
 * 領収書の画面（#39、031、docs/19 §7「請求書の上に載る。小さい」）。
 *   ・明細・合計は請求書の写しで、この画面では直せない（直すなら請求書を直して起こし直す）
 *   ・直せるのは 領収日 と 但し書き だけ。自動保存（打ち終わって 0.5 秒後。共通の仕組み lib/saveQueue.tsx、#177：
 *     重ねて送らない・保存できていなければ 発行・PDF に進まない・離れるときに確かめる）
 *   ・出す（R-0001）：登録番号があれば適格請求書。取消しは請求書と同じ（同じスイッチ）
 */
import { CONFLICT_MSG, RECEIPT_MSG, TAX_MSG, rebaseFields, saveGateText, taxPaperLabel, taxRowsInOrder, type IssueResult, type Receipt, type UnissueResult, UNISSUED_MSG, unissuedReissue, unissuedTitle, headRecipientOf } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { ApiError, api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { DocField, DocHead } from "../components/DocHead.jsx";
import { DateInput } from "../components/DateInput.jsx";
import { UnissueDialog } from "../components/UnissueDialog.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, Field, Help, useToast } from "../components/ui.jsx";
import { useLabels } from "../labels.js";
import { dayJst, md, mdSlash, yen } from "../lib/num.js";
import { ymdShort } from "../lib/dates.js";
import { useSaveQueue } from "../lib/saveQueue.jsx";
import { usePcOnly } from "../lib/pcOnly.js";
import { helpText } from "./Estimate.jsx";
import { DeliveriesLink } from "../components/Deliveries.jsx";

type Head = { issueDate: string; notes: string };

export function ReceiptPage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["receipt", id], queryFn: () => api<Receipt>("GET", `/receipts/${id}`), retry: false });
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/invoices">請求書一覧へ</Link>
        </p>
      </div>
    );
  }
  return <ReceiptEditor key={q.data.id + q.data.status} initial={q.data} />;
}

import { namePageNow, useBackNav, usePageName } from "../components/BackButton.jsx";
import { docShort } from "../components/AreaIcon.jsx";

function ReceiptEditor({ initial }: { initial: Receipt }) {
  const nav = useNavigate();
  const pcOnly = usePcOnly(); // 発行のあと紙の画面へ移るのは PC だけ（スマホは編集画面に残る。PC_ONLY_FEATURES の mailSend と同じ判定）
  const loc = useLocation();
  const qc = useQueryClient();
  const me = useMe();
  const { toast, node: toastNode } = useToast();
  const L = useLabels();
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ その請求書。自動で保存するので、送れていない直しがあるときだけ確かめる（#177）
  const back = useBackNav({ to: `/invoices/${initial.fromInvoice.id}`, label: "請求書" });
  usePageName("領収書", docShort("領収書", initial.documentNo));
  const readOnly = initial.status !== "draft";
  /** #315：頭の宛先（画面は「略称 ／ 支店」・乗せると紙の宛名） */
  const rcp = headRecipientOf({ name: initial.client.name, shortName: initial.client.shortName ?? "", honorific: initial.client.honorific, branch: initial.issued?.recipientBranch ?? initial.client.branch?.name ?? null, contact: null });
  const [head, setHead] = useState<Head>({ issueDate: initial.revision.issueDate, notes: initial.revision.notes });

  /* ---- 自動保存（領収日・但し書きだけ。打ち終わって 0.5 秒後。#177：1本ずつ送る・発行や PDF の前に送り終えるのを待つ・離れるときに確かめる） ---- */
  const latest = useRef(head);
  latest.current = head;
  /**
   * 版（#202、docs/82 §2）：読んだときの版と、そのときの欄（base）。保存は版を付けて送り、ほかで変わっていたら（409 RECEIPT_STALE）
   * 最新を読んで、この画面で修正した欄だけを最新に重ね（rebaseFields）、新しい版で送り直す。通知は CONFLICT_MSG.autoMerged
   */
  const version = useRef(initial.updatedAt);
  const base = useRef<Head>({ issueDate: initial.revision.issueDate, notes: initial.revision.notes });
  const sq = useSaveQueue({
    send: async () => {
      const put = (h: Head) => api<Receipt>("PUT", `/receipts/${initial.id}`, { issueDate: h.issueDate, notes: h.notes, version: version.current });
      const sent = latest.current;
      let r: Receipt;
      try {
        r = await put(sent);
      } catch (e) {
        if (!(e instanceof ApiError && e.code === "RECEIPT_STALE")) throw e;
        const theirs = await api<Receipt>("GET", `/receipts/${initial.id}`);
        const merged = rebaseFields(base.current, latest.current, { issueDate: theirs.revision.issueDate, notes: theirs.revision.notes });
        version.current = theirs.updatedAt;
        setHead(merged);
        latest.current = merged;
        r = await put(merged);
        toast(CONFLICT_MSG.autoMerged);
      }
      version.current = r.updatedAt;
      base.current = { issueDate: r.revision.issueDate, notes: r.revision.notes };
      qc.setQueryData(["receipt", initial.id], r);
    },
  });
  /** 保存が断られた文（直すまで紙の下に出す） */
  const headErr = sq.state.kind === "error" ? sq.state.msg : undefined;
  const setH = (patch: Partial<Head>, immediate = false) => {
    setHead((h) => ({ ...h, ...patch }));
    latest.current = { ...latest.current, ...patch };
    sq.mark(immediate);
  };

  /* ---- 出す・取消し・捨てる ---- */
  const needsCompany = !!me.data?.tenant.needsCompanyName;
  const [issueDlg, setIssueDlg] = useState<{ open: boolean; name: string; busy: boolean; error?: string | undefined }>({ open: false, name: "", busy: false });
  const [unissue, setUnissue] = useState<{ open: boolean; busy: boolean; error?: string | undefined }>({ open: false, busy: false });
  const [askDelete, setAskDelete] = useState(false);
  /** 「領収書発行」：書きかけを送り終えてから窓を開く。保存できていなければ開かずに理由を出す（#177：前は保存に失敗しても発行できた） */
  const openIssue = async () => {
    const why = await sq.ready();
    if (why != null) {
      toast(saveGateText("issue", why), undefined, "error");
      return;
    }
    setIssueDlg((d) => ({ ...d, open: true }));
  };
  /** 下書きの紙：書きかけを送り終えてから紙の画面へ。保存できていなければ移らずに理由を出す（#177） */
  const openDraftPdf = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    void sq.ready().then((why) => (why == null ? nav(`/receipts/${initial.id}/paper`) : toast(saveGateText("pdf", why), undefined, "error")));
  };
  const doIssue = async () => {
    if (needsCompany && !issueDlg.name.trim()) {
      setIssueDlg((d) => ({ ...d, error: RECEIPT_MSG.companyRequired }));
      document.getElementById("rc_co")?.focus();
      return;
    }
    setIssueDlg((d) => ({ ...d, busy: true, error: undefined }));
    try {
      const why = await sq.ready();
      if (why != null) {
        setIssueDlg((d) => ({ ...d, busy: false, error: saveGateText("issue", why) }));
        return;
      }
      const r = await api<IssueResult>("POST", `/receipts/${initial.id}/issue`, needsCompany ? { company: { name: issueDlg.name.trim() } } : {});
      setIssueDlg({ open: false, name: issueDlg.name, busy: false });
      void qc.invalidateQueries({ queryKey: ["invoice", initial.fromInvoice.id] });
      const notice = `領収書ができました。番号 ${r.documentNo} が付きました。`;
      if (!pcOnly.phone) {
        // 2026-10-22：PC は発行が通ったら送信の画面（紙の画面）へ。一言は移った先で出す。「‹ 領収書」で戻れる
        void qc.invalidateQueries({ queryKey: ["receipt", initial.id] });
        namePageNow("領収書", docShort("領収書", r.documentNo)); // 送信の画面の戻るボタンに番号（移る前に。読み直しを待たない）
        nav(`/receipts/${initial.id}/paper`, { state: { notice, noticeTestId: "issued-notice" } });
        return;
      }
      await qc.invalidateQueries({ queryKey: ["receipt", initial.id] });
      // 発行の直後の一言は通知（#171。前は題の下の帯）
      toast(notice, undefined, "success", "issued-notice");
      nav(`/receipts/${initial.id}`, { replace: true, state: { justIssued: r.documentNo } });
      window.scrollTo({ top: 0 });
    } catch (e) {
      setIssueDlg((d) => ({ ...d, busy: false, error: messageOf(e) }));
    }
  };
  const doUnissue = async () => {
    setUnissue((u) => ({ ...u, busy: true, error: undefined }));
    try {
      const r = await api<UnissueResult>("POST", `/receipts/${initial.id}/unissue`, {});
      void qc.invalidateQueries({ queryKey: ["invoice", initial.fromInvoice.id] });
      await qc.invalidateQueries({ queryKey: ["receipt", initial.id] });
      toast(`${r.documentNo} を取り消して下書きに戻しました。`, undefined, "success", "unissued-notice");
      nav(`/receipts/${initial.id}`, { replace: true, state: { unissued: r.documentNo } });
      window.scrollTo({ top: 0 });
    } catch (e) {
      setUnissue((u) => ({ ...u, busy: false, error: messageOf(e) }));
    }
  };
  /** 入金から起こした領収書（E1。元の請求書の結びが無く、入金に結ばれている） */
  const fromPaymentOnly = !!initial.fromPayment && initial.fromInvoice.documentNo === null && initial.fromInvoice.id === initial.id;
  const deleteDraft = async () => {
    try {
      await api<void>("DELETE", `/receipts/${initial.id}`);
      sq.discard();
      void qc.invalidateQueries({ queryKey: ["invoice", initial.fromInvoice.id] });
      // 入金から起こした領収書（E1）は入金の画面へ戻る
      if (fromPaymentOnly) {
        void qc.invalidateQueries({ queryKey: ["payment", initial.fromPayment!.id] });
        nav(`/payments/${initial.fromPayment!.id}`, { replace: true });
        return;
      }
      nav(`/invoices/${initial.fromInvoice.id}`, { replace: true });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const justIssued = (loc.state as { justIssued?: string } | null)?.justIssued;
  const unissued = (loc.state as { unissued?: string } | null)?.unissued;
  const detailCount = initial.lines.filter((l) => l.role === "detail" || l.role === "overhead_rate" || l.role === "discount").length;
  /* 帯の使い方・仕組みの説明は題の右の「？」へ（#92。帯には事実だけ残す） */
  const pageHelp = helpText(
    readOnly
      ? [
          justIssued ? `右上の「PDF 表示」で${L.customer}に渡す領収書になります。` : null,
          initial.canUnissue ? `修正するときは「取消」で下書きに戻します（番号 ${initial.documentNo} はそのままです）。` : null,
        ]
      : unissued || initial.documentNo
        ? // #276：見出しのチップ（番号ありの下書き）の title と同じ1行＋発行の案内（見積書の #273 と同じ形）
          [`${unissuedTitle(unissued ?? initial.documentNo ?? "")}。`, `「領収書発行」を押すと、同じ番号 ${unissued ?? initial.documentNo} で発行します。`]
        : fromPaymentOnly
          ? ["修正できるのは領収日と但し書きだけです。金額は入金の消込額、明細は請求書ごとの行です。金額を修正するときは、この下書きを削除し、入金の配分を修正してから作成し直してください。"]
          : ["修正できるのは領収日と但し書きだけです。金額・明細は請求書の写しです。金額を修正するときは、請求書側を修正してから作成し直してください。"],
  );

  return (
    <>
      {/* #245（docs/94 B-4 案B）：頭は DocHead の3段。2段目＝領収書の絵と名前・宛名（題）・状態｜合計。3段目＝領収日・No.・但し書き */}
      <DocHead
        back={back}
        primary={
          <>
            {readOnly && initial.canUnissue ? (
              <span className="hd-acts">
                <button type="button" className="small" data-testid="unissue-receipt" onClick={() => setUnissue({ open: true, busy: false })}>取り消して下書きに戻す</button>
              </span>
            ) : null}
            {readOnly ? (
              <Link className="btn primary" to={`/receipts/${initial.id}/paper`} data-testid="pdf-link">PDF 表示</Link>
            ) : (
              <button className="btn primary" type="button" onClick={() => void openIssue()} data-testid="issue-receipt">
                <Icon k="lock" />
                領収書発行
              </button>
            )}
          </>
        }
        kind="invoices"
        kindLabel="領収書"
        title={
          // #315：画面は「略称 ／ 支店」（敬称なし）。乗せると紙の宛名
          <span className="dh-ttext" title={rcp.paper} data-testid="doc-client">
            {rcp.name}
            {rcp.branch ? <> ／ {rcp.branch}</> : null}
          </span>
        }
        chips={
          <span className="hd-state" data-testid="head-state">
            {readOnly ? (
              <span className="chip ai" data-testid="receipt-status" title={`${initial.documentNo ?? ""}${initial.issued ? `（${ymdShort(dayJst(initial.issued.at))}発行）` : ""}。発行済みのため修正できません。`}>
                発行済{initial.issued ? ` ${mdSlash(dayJst(initial.issued.at))}` : ""}
              </span>
            ) : (
              // #276・#278：発行を取消して下書きに戻した領収書（番号あり）も「下書き」。番号ありは乗せたときの説明と「？」で
              <span className="chip quiet" data-testid="receipt-status" title={initial.documentNo ? unissuedTitle(initial.documentNo) : undefined}>
                {initial.documentNo ? UNISSUED_MSG.chip : "下書き"}
              </span>
            )}
            {readOnly && initial.issued && !initial.issued.isQualified ? (
              <span className="chip shu" data-testid="receipt-not-qualified" title="登録番号が無いので、適格請求書（インボイス）ではありません">
                適格請求書ではありません
              </span>
            ) : null}
          </span>
        }
        help={pageHelp ? <Help testId="help-receipt" label="領収書の画面の説明" text={pageHelp} /> : null}
        total={{ total: initial.revision.total, subtotal: initial.revision.subtotal, taxTotal: initial.revision.total - initial.revision.subtotal }}
        meta={
          <>
            <DocField label="領収日">
              <DateInput inline label="領収日" ariaLabel="領収日" value={head.issueDate} readOnly={readOnly} clearable={false} onChange={(v) => v && setH({ issueDate: v }, true)} testId="issue-date" />
            </DocField>
            <DocField label="No.">
              <span className="num">{initial.documentNo ?? "未発行"}</span>
            </DocField>
            {/* #223 の2回目：送付の記録（0回なら出さない） */}
            {initial.documentNo ? <DeliveriesLink filter={{ document: initial.id }} testId="receipt-deliveries" /> : null}
          </>
        }
        testId="doc-head"
      />
      <div className="paper head" style={{ maxWidth: 760 }}>
        <Field id="rc_notes" label="但し書き" help={<Help testId="help-receipt-notes" label="但し書きの説明" text="帳票に「但し 〜」と印字されます。" />} style={{ margin: "10px 0 4px" }}>
          <input id="rc_notes" className="inp" value={head.notes} readOnly={readOnly} onChange={(e) => setH({ notes: e.target.value })} />
        </Field>
        <div className="pv" style={{ marginTop: 12 }}>
          <div><span className="muted">件名</span><b>{initial.revision.subject || "（なし）"}</b></div>
          {fromPaymentOnly ? (
            <div><span className="muted">入金</span><b>入金から作成した領収書</b><Link className="btn text" to={`/payments/${initial.fromPayment!.id}`} data-testid="receipt-payment-link">表示</Link></div>
          ) : (
            <div><span className="muted">請求書</span><b className="num">{initial.fromInvoice.documentNo ?? "（下書き）"}</b><Link className="btn text" to={`/invoices/${initial.fromInvoice.id}`}>表示</Link></div>
          )}
          <div><span className="muted">小計（税抜）</span><b className="num">{yen(initial.revision.subtotal)} 円</b></div>
          {/* T2：紙と同じ並び・文字（10% → 8% → 1% → 免税 → 非課税 → 不課税。免税・非課税・不課税は対象額だけ）。立替金は内訳に並べず消費税の下に1行 */}
          {taxRowsInOrder(initial.taxes).map((t) => (
            <div key={`${t.kind}-${t.rate}-${t.reduced}`} data-testid="receipt-tax-row"><span className="muted">{taxPaperLabel(t)}</span>{t.kind === "taxable" ? <b className="num">{yen(t.taxAmount)} 円</b> : null}<span className="muted">（対象 {yen(t.taxableBase)} 円）</span></div>
          ))}
          {initial.revision.reimbursementTotal ? (
            <div data-testid="receipt-reimbursement"><span className="muted">{TAX_MSG.reimbursement}</span><b className="num">{yen(initial.revision.reimbursementTotal)} 円</b></div>
          ) : null}
          <div><span className="muted">合計（税込）</span><b className="num">{yen(initial.revision.total)} 円</b></div>
          <div><span className="muted">明細</span><b className="num">{detailCount} 行</b></div>
        </div>
        <ErrorText>{headErr}</ErrorText>
      </div>
      {!readOnly ? (
        <div className="row" style={{ marginTop: 18, maxWidth: 760, justifyContent: "flex-end" }}>
          {askDelete ? (
            <>
              <span>この下書きを削除しますか（元に戻せません。請求書はそのままです）</span>
              <button type="button" className="small shu" onClick={() => void deleteDraft()}>削除</button>
              <button type="button" className="small" onClick={() => setAskDelete(false)}>取消</button>
            </>
          ) : (
            <>
              <Link className="btn text" to={`/receipts/${initial.id}/paper`} onClick={openDraftPdf} data-leave="own" data-testid="pdf-link">{RECEIPT_MSG.pdfDraft}</Link>
              <span className="sp" />
              <button type="button" className="btn text" onClick={() => setAskDelete(true)}>下書き削除</button>
            </>
          )}
        </div>
      ) : null}
      {issueDlg.open ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !issueDlg.busy && setIssueDlg((d) => ({ ...d, open: false }))}>
          <div className="dlg" role="dialog" aria-label="領収書発行" onKeyDown={(e) => e.key === "Escape" && !issueDlg.busy && setIssueDlg((d) => ({ ...d, open: false }))}>
            <div className="dh">
              発行前の確認
              <Help
                testId="help-issue"
                label="確かめの説明"
                text={helpText([initial.documentNo ? null : "番号の最初は R-0001 です。", "登録番号（設定）があれば適格請求書として印字されます。"])}
              />
            </div>
            <div className="db">
              <div className="pv">
                <div><span className="muted">{L.customer}</span><b>{initial.client.name}{initial.client.branch ? ` ${initial.client.branch.name}` : ""} {initial.client.honorific}</b></div>
                <div><span className="muted">領収金額（税込）</span><b className="num">{yen(initial.revision.total)} 円</b></div>
                <div><span className="muted">領収日</span><b className="num">{md(head.issueDate)}</b></div>
                {initial.documentNo ? (
                  <div><span className="muted">番号</span><b className="num">{initial.documentNo}</b></div>
                ) : (
                  <div><span className="muted">番号</span><b className="num">発行時に付きます</b></div>
                )}
                <div><span className="muted">但し書き</span><b>{head.notes || "（なし）"}</b></div>
              </div>
              {/* #278：番号のある下書き（発行を取消した）は、同じ番号で再発行することを本文の1行で */}
              {initial.documentNo ? <p style={{ margin: 0 }} data-testid="issue-reissue">{unissuedReissue(initial.documentNo)}</p> : null}
              {needsCompany ? (
                <Field id="rc_co" label="会社名（屋号・お名前でも）">
                  <input id="rc_co" className="inp" autoFocus autoComplete="organization" value={issueDlg.name} onChange={(e) => setIssueDlg((d) => ({ ...d, name: e.target.value, error: undefined }))} />
                </Field>
              ) : null}
              <ErrorText>{issueDlg.error}</ErrorText>
              <p className="hint" style={{ margin: 0 }}>発行したあとは、この領収書は修正できません。修正するときは「取消」で下書きに戻して再発行します（番号はそのまま）。</p>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" disabled={issueDlg.busy} onClick={() => setIssueDlg((d) => ({ ...d, open: false }))}>戻って修正</button>
              <button type="button" className="btn primary big" disabled={issueDlg.busy} onClick={() => void doIssue()} data-testid="issue-confirm">
                <Icon k="lock" />
                この内容で発行
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {unissue.open ? (
        // 取消しの窓（#188。見積書・領収書と共用）：題に警告のしるし＋1文。説明は「？」
        <UnissueDialog doc="receipt" no={initial.documentNo ?? ""} busy={unissue.busy} error={unissue.error} onClose={() => setUnissue((u) => ({ ...u, open: false }))} onConfirm={() => void doUnissue()} />
      ) : null}
      {sq.dialog}
      {toastNode}
    </>
  );
}
